import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Fab, Menu, MenuItem, Box, Tooltip } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { RootState } from 'store/rootReducer';

const REDUX_STATE_COOKIE_NAME = 'mindcode_redux_state_backup';
const isReduxStateSaverEnabled = process.env.REACT_APP_ENABLE_REDUX_STATE_SAVER === 'true';

/**
 * Redux 전체 상태를 쿠키에 저장하고 로드할 수 있는 플로팅 버튼 컴포넌트
 * 개발 및 디버깅 용도
 */
function ReduxStateSaver(): JSX.Element | null {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const reduxState = useSelector((state: RootState) => state);
  const open = Boolean(anchorEl);

  if (!isReduxStateSaverEnabled) {
    return null;
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Redux 상태를 localStorage에 저장 (쿠키는 크기 제한이 있어서 localStorage 사용)
  const handleSaveState = () => {
    try {
      const stateToSave = JSON.stringify(reduxState);

      // localStorage에 저장
      localStorage.setItem(REDUX_STATE_COOKIE_NAME, stateToSave);

      // 저장 시간도 함께 저장
      const saveTime = new Date().toLocaleString('ko-KR');
      localStorage.setItem(`${REDUX_STATE_COOKIE_NAME}_time`, saveTime);

      alert(`✅ Redux 상태가 저장되었습니다!\n저장 시간: ${saveTime}`);
      console.log('저장된 상태:', reduxState);
    } catch (error) {
      console.error('상태 저장 실패:', error);
      alert('❌ 상태 저장에 실패했습니다. 콘솔을 확인하세요.');
    }
    handleClose();
  };

  // localStorage에서 Redux 상태를 로드
  const handleLoadState = () => {
    try {
      const savedState = localStorage.getItem(REDUX_STATE_COOKIE_NAME);
      const saveTime = localStorage.getItem(`${REDUX_STATE_COOKIE_NAME}_time`);

      if (!savedState) {
        alert('❌ 저장된 상태가 없습니다.');
        handleClose();
        return;
      }

      const loadedState = JSON.parse(savedState);

      // Redux persist의 localStorage 키를 직접 업데이트
      // persist:root 키에 저장된 상태를 덮어씁니다
      const persistKey = 'persist:root';
      const currentPersistedState = localStorage.getItem(persistKey);

      if (currentPersistedState) {
        const parsedPersist = JSON.parse(currentPersistedState);

        // 각 slice를 업데이트
        if (loadedState.inspection) {
          parsedPersist.inspection = JSON.stringify(loadedState.inspection);
        }
        if (loadedState.project) {
          parsedPersist.project = JSON.stringify(loadedState.project);
        }
        if (loadedState.menu) {
          parsedPersist.menu = JSON.stringify(loadedState.menu);
        }
        if (loadedState.login) {
          parsedPersist.login = JSON.stringify(loadedState.login);
        }

        localStorage.setItem(persistKey, JSON.stringify(parsedPersist));
      }

      alert(`✅ Redux 상태가 로드되었습니다!\n저장 시간: ${saveTime || '알 수 없음'}\n\n페이지를 새로고침합니다.`);
      console.log('로드된 상태:', loadedState);

      // 페이지 새로고침
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (error) {
      console.error('상태 로드 실패:', error);
      alert('❌ 상태 로드에 실패했습니다. 콘솔을 확인하세요.');
    }
    handleClose();
  };

  // 저장된 상태 삭제
  const handleDeleteState = () => {
    const saveTime = localStorage.getItem(`${REDUX_STATE_COOKIE_NAME}_time`);
    if (window.confirm(`저장된 Redux 상태를 삭제하시겠습니까?\n저장 시간: ${saveTime || '알 수 없음'}`)) {
      localStorage.removeItem(REDUX_STATE_COOKIE_NAME);
      localStorage.removeItem(`${REDUX_STATE_COOKIE_NAME}_time`);
      alert('✅ 저장된 상태가 삭제되었습니다.');
    }
    handleClose();
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
      }}
    >
      <Tooltip title="Redux 상태 저장/로드">
        <Fab
          color="primary"
          size="medium"
          onClick={handleClick}
          sx={{
            boxShadow: 3,
            '&:hover': {
              boxShadow: 6,
            },
          }}
        >
          <MoreVertIcon />
        </Fab>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleSaveState}>
          <SaveIcon sx={{ mr: 1 }} fontSize="small" />
          상태 저장
        </MenuItem>
        <MenuItem onClick={handleLoadState}>
          <UploadIcon sx={{ mr: 1 }} fontSize="small" />
          상태 로드
        </MenuItem>
        <MenuItem onClick={handleDeleteState}>
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          상태 삭제
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default ReduxStateSaver;
